import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Divider } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getDashboardData, getAuthToken, checkUserValidity } from './service';
import Header from './Header';
import { getSkuById } from './constants';

export default function FullHistory() {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { userId } = useParams();
  const navigate = useNavigate();
  const effectiveUserId = userId || '1';

  useEffect(() => {
    async function fetchHistoryData() {
      setLoading(true);
      try {
        const token = await getAuthToken();

        if (userId && effectiveUserId === userId) {
          const validation = await checkUserValidity(userId, token);
          if (!validation.isValid) {
            navigate('/');
            return;
          }
        }

        const data = await getDashboardData(effectiveUserId, token);
        setHistoryData(data.transactions);
      } catch (err) {
        console.error('Error fetching full history:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchHistoryData();
  }, [effectiveUserId, userId, navigate]);
  

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h5" align="center">
          Loading...
        </Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h5" align="center" color="error">
          Error: {error.message}
        </Typography>
      </Container>
    );
  }
  
  const sortedTransactions = [...historyData].sort(
    (a, b) => new Date(b.checkout_date) - new Date(a.checkout_date)
  );
  
  const groups = sortedTransactions.reduce((acc, tx) => {
    const dateStr = new Date(tx.checkout_date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
    if (!acc[dateStr]) {
      acc[dateStr] = [];
    }
    acc[dateStr].push(tx);
    return acc;
  }, {});
  
  const sortedGroupKeys = Object.keys(groups).sort(
    (a, b) => new Date(b) - new Date(a)
  );
  
  return (
    <Container maxWidth="sm" sx={{ py: 4, px: 2 }}>
      <Header userId={effectiveUserId} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.3rem', textAlign: 'left' }}>
          Full History
        </Typography>
      </Box>
  
      {sortedGroupKeys.map((dateStr, groupIndex) => (
        <Box key={dateStr} sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.1rem', textAlign: 'left' }}>
            {dateStr}
          </Typography>
          {groups[dateStr].map((item, index) => {
            const skuInfo = getSkuById(item.sku_id);
            return (
              <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <Box
                  component="img"
                  src={skuInfo ? skuInfo.img : null}
                  alt={skuInfo ? skuInfo.label : `SKU ${item.sku_id}`}
                  sx={{ 
                    height: 40, 
                    width: 70,               // Fixed width ensures text alignment
                    mr: 2,                   // Consistent spacing
                    objectFit: 'contain',    // Ensures image fits inside without distortion
                    objectPosition: 'center' // Centers the image within the 70px box
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Checked out{" "}
                    {new Date(item.checkout_date).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                    , {item.location}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Status: {item.status}
                  </Typography>
                  {item.date_charged && (
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#555' }}>
                      Charged: {new Date(item.date_charged).toLocaleString("en-US", {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
          {groupIndex < sortedGroupKeys.length - 1 && (
            <Divider sx={{ my: 2, borderColor: '#ccc', borderBottomWidth: 4 }} />
          )}
        </Box>
      ))}
    </Container>
  );
}